import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgbOffcanvas, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import {
  PROJECT_ADMIN_URLS,
  SUPER_ADMIN_URLS,
  USER_CHECK,
} from 'src/app/core/constants';
import { AuthService } from '../../services/auth-service/auth.service';
import { RoleService } from '../../services/role-service/role.service';
import { MenuItem } from '../../types/Menu';
import { Project, Role } from '../../types/ProjectData';
import { SidebarContentComponent } from './sidebar-content.component';
import { ProjectService } from '../../services/project/project.service';
import { BehaviorSubject, filter } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  template: ``,
})
export class SidebarComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Output() onHide = new EventEmitter<void>();

  private offcanvasService = inject(NgbOffcanvas);
  private offcanvasRef?: NgbOffcanvasRef;
  private activatedRoute = inject(Router);
  private customAuthService = inject(AuthService);
  private roleService = inject(RoleService);
  private projectService = inject(ProjectService);
  private router = inject(Router);
  currentUrl: string = '';
  previousUrl: string = '';
  filteredMenus: MenuItem[] = [];

  KPIMenus: MenuItem[] = [
    {
      label: 'Overall Progress',
      icon: 'SHOW_CHART',
      path: '/s-curve',
    },
    {
      label: 'Engineering Status',
      icon: 'SHOW_CHART',
      path: '/engineering-status',
    },
    {
      label: 'Procurement Status',
      icon: 'SHOW_CHART',
      path: '/procurement-status',
    },
    {
      label: 'Construction Status',
      icon: 'SHOW_CHART',
      path: '/construction-status',
    },
    {
      label: 'Materials Planning',
      icon: 'FILE_COPY',
      path: '/materials-planning',
    },
    { label: 'VDR Status', icon: 'SHOW_CHART', path: '/vdr-status' },
    { label: 'HSE', icon: 'SHOW_CHART', path: '/hse' },
  ];

  commonMenus: MenuItem[] = [
    { label: 'Delay Tracking', icon: 'SHOW_CHART', path: '/delay-tracking' },
    {
      label: 'Delay Submission Form',
      icon: 'FILE_COPY',
      path: '/delay-form/new',
    },
    // { label: 'S Curve', icon: 'SHOW_CHART', path: '/s-curve' },
    { label: 'Forms', icon: 'FILE_COPY', path: '/forms' },
  ];
  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe({
        next: (event) => {
          this.previousUrl = this.currentUrl;
          this.currentUrl = (event as any).url;
        },
      });
  }

  ngOnInit(): void {
    this.projectService.activeKPIs$.subscribe((urls) => {
      this.filteredMenus = this.KPIMenus.filter((menu) =>
        urls.includes(menu.path!)
      );
    });
  }
  ngOnChanges() {
    if (this.visible) {
      this.openOffcanvas();
    } else if (this.offcanvasRef) {
      this.offcanvasRef.dismiss();
    }
  }

  openOffcanvas() {
    this.offcanvasRef = this.offcanvasService.open(SidebarContentComponent, {
      position: 'start',
      scroll: true,
      panelClass: 'sidebar-content',
    });
    this.offcanvasRef.componentInstance.menus = this.menus;
    this.offcanvasRef.result.finally(() => {
      this.onHide.emit();
    });
  }
  goHome = () => {
    if (this.currentUrl === '/assign-user') {
      return;
    }

    const isSuperAdminUrl = SUPER_ADMIN_URLS.includes(this.currentUrl);

    const isProjectAdminUrl = PROJECT_ADMIN_URLS.some((url) =>
      this.currentUrl.startsWith(url)
    );

    if (isSuperAdminUrl || isProjectAdminUrl) {
      return '/';
    } else {
      return '/home';
    }
  };

  get menus(): MenuItem[] {
    const currentUrl = this.currentUrl;
    const isSuperAdminUrl = SUPER_ADMIN_URLS.includes(currentUrl);
    const isProjectAdminUrl = PROJECT_ADMIN_URLS.some((url) =>
      currentUrl.startsWith(url)
    );

    const currentRole: Role = this.roleService.getCurrentRole()!;
    const projectDetails = this.projectService.getStoredProject();

    // const isSuperAdmin = currentRole
    //   ? this.roleService.isCurrentRoleSuperAdmin()!
    //   : false;
    // const isProjectAdmin = projectDetails
    //   ? this.roleService.isCurrentRoleProjectAdmin(projectDetails.projectCode)!
    //   : false;

    const assignUserFromHome =
      this.previousUrl === '/' && this.currentUrl === '/assign-user';

    const ProjectAdminModuleMenu: MenuItem = {
      label: 'Admin Module',
      icon: 'CONTACT_PAGE',
      hidden: isSuperAdminUrl,
      items: [
        {
          label: 'Activity Logs',
          icon: 'DESCRIPTION',
          path: '/admin/activity-logs',
        },
        {
          label: 'Assign User',
          icon: 'PERSON',
          path: '/assign-user',
        },
        {
          label: 'File Uploads',
          icon: 'FILE_COPY',
          items: [
            {
              label: '4D Images',
              icon: 'IMAGE',
              path: '/admin/file-uploads/4d',
              hidden: !projectDetails?.is4d,
            },
            {
              label: 'Reports',
              icon: 'BAR_CHART',
              path: '/admin/file-uploads/report-upload',
              hidden: !projectDetails?.isReports,
            },
            {
              label: 'Videos',
              icon: 'VIDEOCAM',
              path: '/admin/file-uploads/video-upload',
            },
          ],
        },
      ],
    };

    const superAdminMenus: MenuItem[] = [
      { label: 'Home', icon: 'HOME', path: '/', hidden: false },
      {
        label: 'Dashboard Mapping',
        icon: 'DASHBOARD',
        path: '/dashboard-mapping',
        hidden: false,
      },
      // {
      //   label: 'Assign Super Admin',
      //   icon: 'PERSON',
      //   path: '/assign-super-admin',
      //   hidden: false,
      // },
    ];

    if (isSuperAdminUrl || assignUserFromHome || isProjectAdminUrl) {
      return superAdminMenus;
    }

    if (assignUserFromHome || isProjectAdminUrl) {
      return [{ label: 'Home', icon: 'HOME', path: '/', hidden: false }];
    }

    const menus: MenuItem[] = [
      { label: 'Home', icon: 'HOME', path: '/home', hidden: false },
      ...this.filteredMenus.map((menu) => ({
        ...menu,
        hidden: isSuperAdminUrl || isProjectAdminUrl,
      })),
      ...this.commonMenus.map((menu) => ({
        ...menu,
        hidden: isSuperAdminUrl || isProjectAdminUrl,
      })),
    ];

    // if ((isProjectAdmin || isSuperAdmin) && !isProjectAdminUrl) {
    menus.push(ProjectAdminModuleMenu);
    // }

    return menus;
  }
}
